import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  map,
  of,
  scan,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { Instrument } from '@prlw/core/monitor/instrument.entity';
import { LastDealsTableColumn } from '@prlw/core/deals/types/last-deals.types';
import {
  LastDeal,
  TLastDealsSummaryItem,
} from '@prlw/core/deals/entities/last-deal.entity';
import { LastDealsProvider } from '@prlw/core/deals/last-deals.provider';
import {
  OverflowFader,
  OverflowState,
  ScrollableElements,
} from '@prlw/libs/overflow-fader/overflow-fader.class';
import { OverflowFaderFactory } from '@prlw/libs/overflow-fader/overflow-fader-factory.service';
import {
  mapPending,
  mapResult,
  observableWithPending,
} from '@prlw/infrastructure/observableWithPending-v2';

@Component({
  selector: 'prlw-last-deals-table',
  templateUrl: './last-deals-table.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LastDealsTableContainer implements OnInit, OnDestroy {
  @Input() public instrument!: Instrument;
  @Input() public isExpanded: boolean | null = null;

  public data$?: Observable<(LastDeal | TLastDealsSummaryItem)[] | null>;
  public columns$: Observable<LastDealsTableColumn[]>;
  public gridSize$: Observable<string>;
  public loading$: Observable<boolean> = of(false);
  public buttonLoading$: Observable<boolean> = of(false);
  public overflows$: Observable<OverflowState>;
  private readonly overflowFader: OverflowFader;

  constructor(
    private readonly lastDealsProvider: LastDealsProvider,
    overflowFaderFactory: OverflowFaderFactory,
    cdr: ChangeDetectorRef,
  ) {
    this.columns$ = lastDealsProvider.columns$;
    this.gridSize$ = this.lastDealsProvider.gridSize$;
    this.overflowFader = overflowFaderFactory.create(cdr);
    this.overflows$ = this.overflowFader.overflows$;
  }

  private lastDealTimestamp$ = new BehaviorSubject<string>('');
  private nextLastDealTimestamp = '';
  noDealsAnymore = false;

  fetchMoreLabel$ = this.lastDealTimestamp$.pipe(
    map((lastDealTimestamp) =>
      lastDealTimestamp ? 'Показать еще' : 'Показать предыдущие дни',
    ),
  );

  public ngOnInit(): void {
    const getDealsByInstrument$ = this.lastDealTimestamp$.pipe(
      switchMap((lastDealTimestamp) =>
        observableWithPending(
          this.lastDealsProvider.getDealsByInstrument(
            this.instrument.instrumentCode,
            lastDealTimestamp,
          ),
        ),
      ),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );
    const pending$ = getDealsByInstrument$.pipe(mapPending);
    const result$ = getDealsByInstrument$.pipe(
      mapResult,
      tap((items) => {
        if (items.length === 0) {
          this.noDealsAnymore = true;
        }
      }),
    );

    this.loading$ = pending$.pipe(take(2));
    this.buttonLoading$ = pending$;

    this.data$ = result$.pipe(
      scan((acc, curr) => [...acc, ...curr], [] as LastDeal[]),
      tap((items) => {
        this.nextLastDealTimestamp =
          items
            .slice()
            .map((item) => item.timestamp)
            .sort()[0] || '';
      }),
      map((items) =>
        Object.entries(
          Object.groupBy(
            items.map((item) => ({
              ...item,
              date: item.timestamp.split('T')[0],
            })),
            ({ date }) => date,
          ),
        ).reduce(
          (acc, [key, value]) => {
            if (value) {
              return [
                ...acc,
                {
                  date: new Date(key),
                  totalVolume: value.reduce(
                    (nextAcc, nextItem) => nextAcc + nextItem.volume,
                    0,
                  ),
                } as TLastDealsSummaryItem,
                ...value,
              ];
            }
            return acc;
          },
          [] as (LastDeal | TLastDealsSummaryItem)[],
        ),
      ),
    );
  }

  public setScrollHorizontalPosition(scrollLeft: number): void {
    this.overflowFader.setScrollHorizontalPosition(scrollLeft);
  }

  public setScrollVerticalPosition(scrollTop: number): void {
    this.overflowFader.setScrollVerticalPosition(scrollTop);
  }

  public initResizeObserver(elems: ScrollableElements): void {
    this.overflowFader.init(elems);
  }

  public ngOnDestroy(): void {
    this.overflowFader.destroy();
  }

  public handleFetchMore() {
    this.lastDealTimestamp$.next(this.nextLastDealTimestamp);
  }
}
